import * as React from 'react';
import type { AutomationAction } from '../../../MedicareProAPI/Entities/Automation/AutomationAction';
import { AUTOMATION_ACTION_TYPE } from '../../../MedicareProAPI/Entities/Automation/AutomationAction';
import {
  AUTOMATION_TRIGGER_RECIPE_LABELS,
  AUTOMATION_ACTION_TYPE_LABELS,
  AUTOMATION_ACTION_TYPE_ICONS,
  AUTOMATION_TRIGGER_DATA_FIELD_TYPE_LABELS,
} from '../../Language/en';
import { Utilities } from '../../../MedicareProAPI/Utilities/Utilities';
import { AutomationActionFormAction } from './AutomationActionFormAction';
import { AutomationActionFormTrigger } from './AutomationActionFormTrigger';
import { SelectMap } from '../../Utilities/SelectMap';
import type {
  AUTOMATION_ACTION_TRIGGER_TYPE,
  AUTOMATION_TRIGGER_COMBINATION,
  Automation,
  AutomationActionTriggerData,
} from '../../../MedicareProAPI/Entities/Automation/Automation';
import {
  AUTOMATION_TRIGGER_DATA_FIELD_TYPE,
  AutomationPayload,
} from '../../../MedicareProAPI/Entities/Automation/Automation';
import StyledModal from '../StyledModal';
import { SIDETAB_POSITION, SideTab } from '../SideTab';
import { AutomationRepository } from '../../../MedicareProAPI/Repositories/Automation/AutomationRepository';
import { AutomationActionCard } from './Action/AutomationActionCard';
import { AUTOMATION_ACTION_TRIGGER_RECIPES } from '../../Panels/AgentAutomationManagePanel'; // TODO: Move this

export interface AutomationFormProps {
  automation: Automation;
  automationActions: AutomationAction[];
  userIsAgencyPrinciple?: boolean;
  agencyHasLoaAgents?: boolean;
  sharedAutomations: Automation[]; // TODO: Define type
  userAccessToken: string;
  userId: number;
  onSave: (automation: Automation) => Promise<Automation>;
  onShare: (automation: Automation) => Promise<Automation>;
  onDelete: (automation: Automation) => Promise<Automation>;
  onChangeParentAutomation: (
    automation: Automation,
    parentAutomation: Automation
  ) => Promise<Automation>;
}

export const AutomationForm = (props: AutomationFormProps): React.JSX.Element => {
  const [automation, setAutomation] = React.useState<Automation>(null);
  const [automationActions, setAutomationActions] = React.useState<AutomationAction[]>(null);

  const [automationId, setAutomationId] = React.useState(null);
  const [automationTriggerEntity, setAutomationTriggerEntity] = React.useState<string>(null);
  const [automationTriggerType, setAutomationTriggerType] =
    React.useState<AUTOMATION_ACTION_TRIGGER_TYPE>(null);
  const [automationTriggerData, setAutomationTriggerData] =
    React.useState<AutomationActionTriggerData[]>();
  const [automationRecipe, setAutomationRecipe] =
    React.useState<AUTOMATION_TRIGGER_COMBINATION>(null);
  const [automationParentRecipe, setAutomationParentRecipe] =
    React.useState<AUTOMATION_TRIGGER_COMBINATION>(null);
  const [automationName, setAutomationName] = React.useState<string>(null);
  const [automationParentAutomationId, setAutomationParentAutomationId] =
    React.useState<number>(null);
  const [automationIsActive, setAutomationIsActive] = React.useState<boolean>(false);
  const [automationIsShared, setAutomationIsShared] = React.useState<boolean>(false);
  const [automationCanOverride, setAutomationCanOverride] = React.useState<boolean>(false);

  const [expanded, setExpanded] = React.useState<boolean>(false);

  const [shareModal, setShareModal] = React.useState(false);
  const [deleteModal, setDeleteModal] = React.useState(false);

  const [deleting, setDeleting] = React.useState(false);
  const [sharing, setSharing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const statuses = [
    { value: true, label: 'Active' },
    { value: false, label: 'Inactive' },
  ];

  const noYes = [
    { value: false, label: 'No' },
    { value: true, label: 'Yes' },
  ];

  /**
   * Pass instance of this Automation to it's parent onSave handler
   * that handles create/update process
   *
   * @param {Automation} automation - Automation to share
   * @returns {Promise<Automation>} Returned automation
   */
  const handleSaveAutomation = (automation: Automation) => {
    setSaving(true);

    automation
      .setName(automationName)
      .setRecipe(automationRecipe)
      .setCanOverride(automationCanOverride)
      .setIsActive(automationIsActive)
      .setTriggerEntity(automationTriggerEntity)
      .setTriggerData(automationTriggerData)
      .setTriggerType(automationTriggerType);

    return props.onSave(automation).then((automation) => {
      setSaving(false);

      setAutomation(automation);
      setAutomationId(automation.getId());
    });
  };

  /**
   * Pass instance of this Automation to it's parent onShare handler
   * that handles share process
   *
   * @param {Automation} automation - Automation to share
   * @returns {Promise<Automation>} Returned automation
   */
  const handleShareAutomation = (automation: Automation) => {
    setSharing(true);

    return props.onShare(automation).then((automation) => {
      setSharing(false);
      setShareModal(false);
    });
  };

  /**
   * Pass instance of this Automation to it's parent onDelete handler
   * that handles deletion process
   *
   * @param {Automation} automation - Automation to share
   * @returns {Promise<Automation>} Returned (deleted) automation
   */
  const handleDeleteAutomation = (automation: Automation) => {
    setSharing(true);

    props.onDelete(automation).then((automation) => {
      setDeleting(false);
      setDeleteModal(false);
    });
  };

  /**
   * Pass instance of this Automation to it's parent onChangeParentAutomation handler
   * that handles the parent automation being changes
   *
   * @param {Automation} automation - Automation to share
   * @param {Automation} parentAutomation - Automation to assign as the parent autoamtion
   * @returns {Promise<Automation>} Returned (deleted) automation
   */
  const handleChangeParentAutomation = (automation: Automation, parentAutomation: Automation) => {
    setAutomationParentAutomationId(parentAutomation.getId());
    setSaving(true);

    props.onChangeParentAutomation(automation, parentAutomation).then((automation) => {
      setSaving(false);
      handleAutomationChange(automation);
    });
  };

  // Handle deleting the old and inserting the new actions
  const handleChangeParentAutomationId = (automation: Automation, parentAutomationId: number) => {
    const parentAutomation = props.sharedAutomations.find(
      (sharedAutomation) => sharedAutomation.getId() === parentAutomationId
    );
    handleChangeParentAutomation(automation, parentAutomation);
  };

  const clearSelectedAutomation = () => {
    setAutomation(null); // TODO

    setAutomationId(null);
    setAutomationRecipe(null);
    setAutomationParentRecipe(null);
    setAutomationTriggerData(null);
    setAutomationTriggerEntity(null);
    setAutomationTriggerType(null);
    setAutomationName(null);
    setAutomationIsActive(null);
    setAutomationIsShared(null);
    setAutomationCanOverride(null);
    setAutomationParentAutomationId(null);
  };

  const handleAutomationChange = (automation: Automation, expand: boolean = null) => {
    setAutomation(automation); // TODO

    setAutomationId(automation.getId());
    setAutomationRecipe(automation.getRecipe());
    setAutomationTriggerData(automation.getTriggerData());
    setAutomationTriggerEntity(automation.getTriggerEntity());
    setAutomationTriggerType(automation.getTriggerType());
    setAutomationName(automation.getName());
    setAutomationIsActive(automation.getIsActive());
    setAutomationIsShared(automation.getIsShared());
    setAutomationCanOverride(automation.getCanOverride());
    setAutomationParentAutomationId(
      automation.getParentAutomation() ? automation.getParentAutomation().getId() : null
    );

    if (automation.getParentAutomation()) {
      setAutomationParentRecipe(automation.getParentAutomation().getRecipe());
    }

    if (expand === false) {
      setExpanded(false);
    }
  };

  React.useEffect(() => {
    clearSelectedAutomation();

    if (props.automation) {
      handleAutomationChange(props.automation);

      if (!props.automation.getId()) {
        setExpanded(true);
      }
    }
  }, [props.automation]);

  return (
    <>
      {automation && (
        <>
          <StyledModal
            size="md"
            title="Share Automation"
            footerCloseButton={true}
            footerButtons={
              <button
                onClick={(e) => handleShareAutomation(automation)}
                className="btn btn-success"
                disabled={
                  (automationActions &&
                    automationActions.filter((action) => action.getId()).length > 0) === false
                }
                type="button"
              >
                {sharing ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Sharing
                  </>
                ) : (
                  <>Share</>
                )}
              </button>
            }
            show={shareModal}
            onClose={(e) => setShareModal(false)}
          >
            <div className="row pl-3 pr-3">
              {automationActions &&
                automationActions.filter((action) => action.getId()).length > 0 && (
                  <p>
                    Are you sure you want to turn this automation into a template? Templating an
                    automation means it can no longer be edited because it will be available for use
                    by all of your downlines. You will also lose access to this automation and will
                    need to make a new one, selecting this template as the base automation.
                  </p>
                )}
              {(!automationActions ||
                automationActions.filter((action) => action.getId()).length === 0) && (
                <p>
                  Your automation needs to have at least one action to be turned into a template.
                  Templated automation become available for downline agent use.{' '}
                </p>
              )}
            </div>
          </StyledModal>
          <StyledModal
            size="md"
            title="Delete Automation"
            footerCloseButton={true}
            footerButtons={
              <button
                onClick={(e) => handleDeleteAutomation(automation)}
                className="btn btn-danger"
                type="button"
              >
                {deleting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Deleting
                  </>
                ) : (
                  <>Delete</>
                )}
              </button>
            }
            show={deleteModal}
            onClose={(e) => setDeleteModal(false)}
          >
            <div className="row pl-3 pr-3">
              <p>Are you sure you want to delete this automation? This can't be undone.</p>
            </div>
          </StyledModal>

          <h5 className="mt-2 font-weight-bold mb-4 calMess">
            <i className="far fa-chart-network mr-2 "></i>
            {automationName}
            {automationParentAutomationId && (
              <div className="badge badge-info ml-2 float-right" style={{ fontSize: '9pt' }}>
                Templated Automation
              </div>
            )}
          </h5>

          <div className="card p-3 mb-3 direction-s card-tall">
            {!expanded && (
              <>
                <div className="row">
                  <div className="col-12 text-center cp" onClick={(e) => setExpanded(!expanded)}>
                    <h6 className="mb-0">
                      {automationRecipe && (
                        <span className="badge badge-pill badge-primary p-2 px-3">
                          {
                            AUTOMATION_TRIGGER_RECIPE_LABELS.find(
                              (recipe) => recipe.value === automationRecipe
                            ).label
                          }
                        </span>
                      )}
                      {!automationRecipe && automationParentRecipe && (
                        <span className="badge badge-pill badge-primary p-2 px-3">
                          {
                            AUTOMATION_TRIGGER_RECIPE_LABELS.find(
                              (recipe) => recipe.value === automationParentRecipe
                            ).label
                          }
                        </span>
                      )}
                      {!automationRecipe && !automationParentRecipe && (
                        <span className="badge badge-pill border border-danger p-2">
                          <i>
                            <i className="far fa-exclamation-circle mr-1 text-danger"></i>Your
                            automation has no triggers, click to set up.
                          </i>
                        </span>
                      )}
                    </h6>
                  </div>
                </div>
              </>
            )}
            {expanded && (
              <>
                <div className="mb-3">
                  <h6>
                    <span className=" font-weight-bold calMess">Automation settings</span>
                    {automationName && (
                      <span
                        className="float-right badge cp border border-primary calMess"
                        onClick={(e) => setExpanded(!expanded)}
                      >
                        Close
                      </span>
                    )}
                  </h6>
                </div>
                <div className="row">
                  <div className="mb-2 col-12 col-lg-4">
                    <label className="label">Automation Name</label>
                    <div className="input-group input-group-sm">
                      <input
                        disabled={saving}
                        type="text"
                        className="form-control border-secondary"
                        autoComplete="off"
                        value={automationName || ''}
                        onChange={(e) => setAutomationName(e.target.value)}
                      />
                    </div>
                  </div>
                  {automationId && (
                    <>
                      <div className="mb-2 col-lg-4">
                        <label className="label">Automation Status</label>
                        <div className="input-group input-group-sm">
                          <select
                            disabled={saving}
                            className="form-control form-control-sm"
                            onChange={(e) => setAutomationIsActive(e.target.value === '1')}
                          >
                            {statuses.map((item) => (
                              <option
                                selected={automationIsActive === item.value}
                                value={item.value ? 1 : 0}
                              >
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {automationIsShared === true && (
                          <small>
                            Disabling a shared automation will disable it for all users.
                          </small>
                        )}
                      </div>
                      {props.userIsAgencyPrinciple && props.agencyHasLoaAgents && (
                        <div className="mb-2 col-lg-4">
                          <label className="label">Can be Overridden?</label>
                          <div className="input-group input-group-sm">
                            <select
                              disabled={saving}
                              className="form-control form-control-sm"
                              onChange={(e) => setAutomationCanOverride(e.target.value === '1')}
                            >
                              {noYes.map((item) => (
                                <option
                                  selected={automationCanOverride === item.value}
                                  value={item.value ? 1 : 0}
                                >
                                  {item.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <small>
                            If an LOA has trigger of the same type, does it replace this one?
                          </small>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {automationId && automationIsShared === false && (
                  <div className="mb-2">
                    {props.sharedAutomations &&
                      props.sharedAutomations.filter((item) => !item.getIsDeleted()).length > 0 && (
                        <>
                          <label className="label">
                            {!automationParentAutomationId && (
                              <> Use Template Automation or create your own?</>
                            )}
                            {automationParentAutomationId && <>Template Automation being used</>}
                          </label>
                          <div className="input-group input-group-sm">
                            <select
                              disabled={saving || automationParentAutomationId > 0}
                              className="form-control form-control-sm"
                              onChange={(e) =>
                                handleChangeParentAutomationId(automation, parseInt(e.target.value))
                              }
                            >
                              <option>Create my own</option>
                              {props.sharedAutomations
                                .filter((item) => !item.getIsDeleted())
                                .map((item) => (
                                  <option
                                    selected={automationParentAutomationId === item.getId()}
                                    value={item.getId()}
                                  >
                                    {item.getName()}
                                  </option>
                                ))}
                            </select>
                          </div>
                        </>
                      )}
                  </div>
                )}
                {
                  //&& (automationIsShared === false && !automationParentAutomationId)
                  automationId && (
                    <>
                      <div className="mb-3 mt-4">
                        <h6>
                          <span className=" font-weight-bold calMess">
                            What event should start this automation?
                          </span>
                        </h6>
                      </div>
                      <div className="mb-3">
                        <label className="label d-block">Event Trigger</label>
                        <div className="input-group input-group-sm">
                          <select
                            disabled={
                              saving ||
                              automationIsShared ||
                              (automationParentAutomationId ? true : false)
                            }
                            className="form-control form-control-sm"
                            onChange={(e) => {
                              const typeValue = e.target.value as AUTOMATION_TRIGGER_COMBINATION;
                              const recipe = AUTOMATION_ACTION_TRIGGER_RECIPES.find(
                                (recipe) => recipe.value === typeValue
                              );

                              if (recipe === undefined) {
                                setAutomationTriggerEntity(undefined);
                                setAutomationTriggerType(undefined);
                                setAutomationTriggerData(null);
                                setAutomationRecipe(null);
                                setAutomationParentRecipe(null);
                              } else {
                                setAutomationRecipe(typeValue);
                                setAutomationTriggerEntity(recipe.entity);
                                setAutomationTriggerType(recipe.type);

                                if (recipe.field) {
                                  const newValue = [
                                    {
                                      field: recipe.field,
                                      required: true,
                                    },
                                  ];

                                  setAutomationTriggerData(newValue);
                                } else {
                                  setAutomationTriggerData(null);
                                }
                              }
                            }}
                          >
                            <option></option>
                            {AUTOMATION_TRIGGER_RECIPE_LABELS.map((item) => {
                              const selected = item.value === automationRecipe;
                              return (
                                <option selected={selected} value={item.value}>
                                  {item.label}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                        {automationRecipe === 'ind_birthday' && (
                          <div className="badge badge-pill table-warning text-dark p-1 px-1 mt-2">
                            <i>
                              Birthday automations that have an action that sends mail will trigger
                              a week early
                            </i>
                          </div>
                        )}
                      </div>
                      {automationTriggerType && (
                        <div className="mb-2">
                          <AutomationActionFormTrigger
                            key={automationId}
                            automationId={automationId}
                            userId={props.userId}
                            userAccessToken={props.userAccessToken}
                            recipe={automationRecipe}
                            triggerData={automationTriggerData}
                            shared={automationIsShared}
                            automationHasParent={automationParentAutomationId ? true : false}
                            onChange={(newFormData) => {
                              setAutomationTriggerData(newFormData);
                            }}
                          />
                        </div>
                      )}
                    </>
                  )
                }
                <div className="row mt-3">
                  <div className="col-9">
                    <div
                      onClick={(e) => handleSaveAutomation(automation)}
                      className="btn btn-sm btn-primary d-inline-block "
                    >
                      {automationId ? (
                        saving ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i> Saving
                          </>
                        ) : (
                          <>Save</>
                        )
                      ) : saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Creating
                        </>
                      ) : (
                        <>Create</>
                      )}
                    </div>
                    {!automationParentAutomationId &&
                      automationId &&
                      props.userIsAgencyPrinciple &&
                      automationIsShared === false && (
                        <div
                          onClick={(e) => setShareModal(true)}
                          className="btn btn-sm btn-warning d-inline-block ml-2"
                          style={{ fontSize: '9pt' }}
                        >
                          Make Template
                        </div>
                      )}
                  </div>
                  <div className="col align-self-end">
                    {automationId && (
                      <div
                        onClick={(e) => setDeleteModal(true)}
                        className="align-text-bottom cp text-danger float-right"
                      >
                        {deleting ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i> Deleting
                          </>
                        ) : (
                          <i className="far fa-trash-alt"></i>
                        )}
                      </div>
                    )}
                  </div>
                  {automationIsShared ||
                    ((automationParentAutomationId ? true : false) && (
                      <>
                        <div className="badge badge-pill table-warning text-dark p-1 px-1 mt-2 ml-2">
                          <i>
                            You cannot edit trigger or criteria values on a Templated Automation.
                          </i>
                        </div>
                      </>
                    ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};
